<template>
  <div class="chart-container" style="position: relative; height: 400px; width: 100%; padding-top: 20px;">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'

// ลงทะเบียน Chart.js Components
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// รับค่าข้อมูลจาก Parent Component
const props = defineProps({
  transactions: {
    type: Array,
    required: true,
    default: () => []
  }
})

// --- ส่วนหัวใจหลัก: การทำ Data Transformation แบบ Dynamic ---
const chartData = computed(() => {
  // 1. ดึงช่วงเวลา (Labels) ทั้งหมดที่มีในข้อมูลมาเรียงลำดับ
  const allTimes = [...new Set(props.transactions.map(item => item.time.substring(0, 5)))].sort()

  // 2. กำหนดประเภทธุรกรรมและสี
  const typeConfigs = [
    { key: 'PAWN',          label: 'จำนำ',    color: '#4A90E2' },
    { key: 'INTEREST',      label: 'ต่อดอก',  color: '#FF8A65' },
    { key: 'INC_PRINCIPLE', label: 'เพิ่มต้น',  color: '#66BB6A' },
    { key: 'DEC_PRINCIPLE', label: 'ลดต้น',   color: '#EF5350' },
    { key: 'REDEEM',        label: 'ไถ่ถอน',  color: '#FBC02D' }
  ]

  // 3. สร้าง Datasets
  const datasets = typeConfigs.map(config => ({
    label: config.label,
    backgroundColor: config.color,
    data: allTimes.map(time => {
      const found = props.transactions.find(
        item => item.time.startsWith(time) && item.type === config.key
      )
      return found ? found.total : 0
    })
  }))

  return {
    labels: allTimes,
    datasets: datasets
  }
})

// การตั้งค่ากราฟให้เป็นแบบ Stacked ตามรูปต้นฉบับ
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { color: '#000', width: 2 },
      ticks: {
        color: '#333',
        font: { size: 10 },
        autoSkip: false, // ยังคงแสดงแท่งกราฟครบทุกช่อง ไม่ถูกโดดข้าม
        maxRotation: 0,
        
        // 🛠️ จุดที่เพิ่มเข้ามา: ซ่อนเฉพาะตัวอักษรบอกเวลาที่ลงท้ายด้วย :30
        callback: function(value) {
          // ดึงค่า Label จริงออกมา เช่น "15:30" หรือ "15:00"
          const label = this.getLabelForValue(value);
          
          // ถ้าข้อความลงท้ายหรือมีคำว่า :30 ให้คืนค่าว่างเพื่อซ่อนตัวอักษร
          if (label && label.includes(':30')) {
            return '';
          }
          
          return label; // ถ้าเป็นชั่วโมงเต็ม (เช่น 15:00) ให้แสดงปกติ
        }
      },
      title: {
        display: true,
        text: 'เวลา ▶',
        align: 'end',
        color: '#000',
        font: { weight: 'bold' }
      }
    },
    y: {
      stacked: true,
      beginAtZero: true,
      border: { color: '#000', width: 2 },
      grid: { display: true },
      ticks: { display: true },
      title: {
        display: true,
        text: 'จำนวนธุรกรรม ▶',
        align: 'end',
        color: '#000',
        font: { weight: 'bold' }
      }
    }
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { size: 12 }
      }
    }
  }
}
</script>
